import React, { useCallback, useMemo, useRef, useState } from "react";
import { Point } from "./Geometry";
import { Action, DragAction, PrimaryAction } from "./Actions";
import { MeasurementsRenderer } from "./MeasurementRenderer/MeasurementsRenderer";
import { InputMeasurement, LineMeasurement } from "../../data/Measurements/Measurement";
import { useMeasurement } from "../../providers/NewMeasurementProvider";

// MARK: Constants

const MAX_ZOOM = 5;
const MIN_ZOOM = 0.4;

const MAX_OFFSET = 5000;
const MIN_OFFSET = -5000;

type CanvasViewportProviderContextType = {
  /**
   * Sets the canvas element that is being used. Must be called by a child component.
   */
  setCanvas: (canvas: HTMLCanvasElement | null) => void;

  /**
   * The offset (translation) of the canvas.
   */
  offset: Point;

  /**
   * Sets the offset of the canvas.
   */
  setOffset: (offset: Point) => void;

  /**
   * The scale (zoom) of the canvas.
   */
  scale: number;

  /**
   * Sets the scale of the canvas
   */
  setScale: (scale: number) => void;

  /*
  * The viewport's virtual height.
  */
  virtualHeight: number;

  /**
   * The viewport's virtual width.
   */
  virtualWidth: number;

  /**
   * Converts from REAL (canvas) coordinates to VIRTUAL coordinates.
   */
  realToVirtual: (point: Point) => Point;

  /**
   * Converts from VIRTUAL coordinates to REAL (canvas) coordinates.
   */
  virtualToReal: (point: Point) => Point;

  /*
  * The position of the mouse, on page coordinates.
  */
  pageMousePosition: Point;

  /*
  * Sets the position of the mouse, on page coordinates.
  */
  setPageMousePosition: (position: Point) => void;

  /*
  * The position of the mouse, on canvas coordinates.
  */
  canvasMousePosition: Point;

  /*
  * The position of the mouse, on virtual coordinates.
  */
  virtualMousePosition: Point;

  /*
  * The measurement keys that are currently focused.
  */
  focusedKeys: string[] | null;

  /*
  * Sets the measurement keys that are currently focused.
  */
  setFocusedKeys: (keys: string[] | null) => void;

  /*
  * The key of the measurement that is currently hovered.
  */
  hoveredKey: string | null;

  /*
  * Sets the key of the measurement that is currently hovered.
  */
  setHoveredKey: (key: string | null) => void;

  /**
   * Pans the canvas by the given amount. The duration of the animation can be specified.
   */
  pan: (dx: number, dy: number, duration?: number) => void;

  /**
   * Pans the canvas to the given position. The duration of the animation can be specified.
   */
  panToPosition: (position: Point, duration?: number) => void;

  /**
   * Fits the canvas offset and scale to the given measurement.
   */
  fitToMeasurement: (measurement: LineMeasurement) => void;

  /**
   * The key of the measurement that was just created. This will be set ONCE everytime a measurement is created.
   * And will be reset to null on the VERY NEXT render.
   */
  measurementKeyThatWasJustCreated: string | null;

  /**
   * Sets the key of the measurement that was just created
   */
  setMeasurementKeyThatWasJustCreated: (key: string | null) => void;

  /**
   * The state of the context menu for a measurement.
   */
  measurementContextMenu: MeasurementContextMenuState;

  /**
   * Opens the context menu for a measurement.
   */
  openMeasurementContextMenu: (measurement: InputMeasurement, position: Point) => void;

  /**
   * Closes the context menu.
   */
  closeMeasurementContextMenu: () => void;

  /**
   * The primary action that is currently being performed.
   */
  primaryAction: PrimaryAction;

  /**
   * Sets the primary action that is currently being performed.
   */
  setPrimaryAction: (action: PrimaryAction) => void;

  /**
   * The drag action that is currently being performed.
   */
  dragAction: DragAction;

  /**
   * Sets the drag action that is currently being performed.
   */
  setDragAction: (action: DragAction) => void;

  /**
   * The key of the measurement that is currently being dragged, if any.
   */
  measurementKeyBeingDragged: string | null;

  /**
   * Sets the key of the measurement that is currently being dragged.
   */
  setMeasurementKeyBeingDragged: (key: string | null) => void;
}

type MeasurementContextMenuState = {
  /**
   * Whether the context menu is currently active.
   */
  active: boolean;

  /**
   * The position of the context menu in CANVAS coordinates.
   */
  position?: Point;

  /**
   * The measurement that the context menu is for.
   */
  measurement?: InputMeasurement;
}

const CanvasViewportContext = React.createContext<CanvasViewportProviderContextType | null>(null);

type CanvasViewportProviderArguments = {
  children: React.ReactNode;
}

export const CanvasViewportProvider: React.FC<CanvasViewportProviderArguments> = ({ children }) => {
  /**
   * The reference to the canvas element. Must be set on the by a child component.
   */
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);

  /**
   * The offset of the canvas.
   */
  const [offset, setOffset] = useState<Point>({ x: 0, y: 0 });

  /**
   * The scale of the canvas.
  */
  const [scale, setScale] = useState<number>(1);

  /**
   * The keys of the measurements that are currently focused, if any.
   */
  const [focusedKeys, setFocusedKeys] = useState<string[] | null>(null);

  /**
   * The key of the measurement that is currently hovered, if any.
   */
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);

  /**
   * The state of the context menu for a measurement, if any.
   */
  const [measurementContextMenu, setMeasurementContextMenu] = useState<MeasurementContextMenuState>({ active: false });

  /**
   * The primary action that is currently being performed.
   */
  const [primaryAction, setPrimaryAction] = useState<PrimaryAction>('action-start-next-measurement');

  /**
   * The drag action that is currently being performed.
   */
  const [dragAction, setDragAction] = useState<DragAction>('action-pan');

  /**
   * The key of the measurement that is currently being dragged, if any.
   */
  const [measurementKeyBeingDragged, setMeasurementKeyBeingDragged] = useState<string | null>(null);

  /**
   * The virtual height of the canvas.
   */
  const virtualHeight = useMemo(() => {
    if (canvas === null) {
      return 0;
    }

    return canvas.clientHeight / scale;
  }, [canvas, scale]);

  /**
   * The virtual width of the canvas.
   */
  const virtualWidth = useMemo(() => {
    if (canvas === null) {
      return 0;
    }

    return canvas.clientWidth / scale;
  }, [canvas, scale]);

  const realToVirtual = useCallback((point: Point): Point => {
    return {
      x: point.x / scale - offset.x,
      y: point.y / scale - offset.y
    };
  }, [offset, scale]);

  /**
   * Converts from VIRTUAL coordinates to REAL coordinates.
   */
  const virtualToReal = useCallback((point: Point): Point => {
    return {
      x: (point.x + offset.x) * scale,
      y: (point.y + offset.y) * scale
    };
  }, [offset, scale]);

  /**
  * The position of the mouse, on page coordinates.
  */
  const [pageMousePosition, setPageMousePosition] = useState<Point>({ x: 0, y: 0 });

  /**
   * The position of the mouse, on canvas coordinates.
   */
  const canvasMousePosition = useMemo(() => {
    if (canvas === null) {
      return { x: 0, y: 0 };
    }

    const rect = canvas.getBoundingClientRect();

    return {
      x: pageMousePosition.x - rect.left,
      y: pageMousePosition.y - rect.top
    };
  }, [canvas, pageMousePosition]);

  /**
   * The position of the mouse, on virtual coordinates.
   */
  const virtualMousePosition = useMemo(() => {
    return realToVirtual(canvasMousePosition);
  }, [canvasMousePosition, realToVirtual]);


  // MARK: Helper UI Functions

  const pan = useCallback((dx: number, dy: number, duration: number = 0) => {
    const newOffset = {
      x: offset.x + dx,
      y: offset.y + dy
    };

    // Clamp the new offset to the min and max values
    const clampedOffset = {
      x: Math.max(MIN_OFFSET, Math.min(MAX_OFFSET, newOffset.x)),
      y: Math.max(MIN_OFFSET, Math.min(MAX_OFFSET, newOffset.y))
    };

    // Use a smooth animation to transition to the new offset
    const startTime = performance.now();
    const animate = () => {
      const elapsedTime = performance.now() - startTime;
      const progress = Math.min(elapsedTime / duration, 1);
      const newX = offset.x + (clampedOffset.x - offset.x) * progress;
      const newY = offset.y + (clampedOffset.y - offset.y) * progress;
      setOffset({ x: newX, y: newY });
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [offset, setOffset]);

  const panToPosition = useCallback((position: Point, duration: number = 0) => {
    const dx = position.x - virtualWidth / 2;
    const dy = position.y - virtualHeight / 2;
    pan(dx, dy, duration);
  }, [pan, virtualHeight, virtualWidth]);

  /**
   * Fits the canvas offset and scale to the given measurement.
   */
  const fitToMeasurement = useCallback((measurement: LineMeasurement) => {
    if(!canvas) {
      return;
    }

    const dx = measurement.startPosition.x - virtualWidth / 2;
    const dy = measurement.startPosition.y - virtualHeight / 2;

    const padding = 200;

    const newScale = Math.min(
      (canvas.clientWidth - padding) / Math.abs(measurement.endPosition.x - measurement.startPosition.x),
      (canvas.clientHeight - padding) / Math.abs(measurement.endPosition.y - measurement.startPosition.y)
    );

    if(newScale > MAX_ZOOM || newScale < MIN_ZOOM) {
      return;
    }

    setScale(newScale);
    pan(dx, dy);
  }, [pan, virtualHeight, virtualWidth, canvas]);

  const openMeasurementContextMenu = useCallback((measurement: InputMeasurement, position: Point) => {
    setMeasurementContextMenu({
      active: true,
      measurement,
      position
    });
  }, [setMeasurementContextMenu]);

  const closeMeasurementContextMenu = useCallback(() => {
    setMeasurementContextMenu({
      active: false
    });
  }, [setMeasurementContextMenu]);

  const [measurementKeyThatWasJustCreated, setMeasurementKeyThatWasJustCreated] = useState<string | null>(null);

  return (
    <CanvasViewportContext.Provider value={{
      setCanvas,
      offset,
      setOffset,
      scale,
      setScale,
      virtualHeight,
      virtualWidth,
      realToVirtual,
      virtualToReal,
      pageMousePosition,
      setPageMousePosition,
      canvasMousePosition,
      virtualMousePosition,
      focusedKeys,
      setFocusedKeys,
      hoveredKey,
      setHoveredKey,
      pan,
      panToPosition,
      fitToMeasurement,
      measurementKeyThatWasJustCreated,
      setMeasurementKeyThatWasJustCreated,
      measurementContextMenu,
      openMeasurementContextMenu,
      closeMeasurementContextMenu,
      primaryAction,
      setPrimaryAction,
      dragAction,
      setDragAction,
      measurementKeyBeingDragged,
      setMeasurementKeyBeingDragged
    }}>
      {children}
    </CanvasViewportContext.Provider>
  );
}

export const useCanvasViewport = () => {
  const context = React.useContext(CanvasViewportContext);

  if (!context) {
    throw new Error("useCanvasViewport must be used within a CanvasViewportProvider");
  }

  return context;
}